import React, { useRef, useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { painPoints } from '../../data/values';
import useWordReveal from '../../hooks/useWordReveal';
import { CircuitTrace, BrokenChain, Microchip, CodeTags, DataFlow } from '../../components/Doodle/Doodle';
import { IsoBolt, FloatingDiamond } from '../../components/Svg3D/Svg3D';
import styles from './Values.module.css';

const SeverityBar = ({ severity, visible, delay }) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setWidth(severity), delay + 400);
      return () => clearTimeout(timer);
    }
  }, [visible, severity, delay]);

  const color =
    severity >= 85 ? '#E05252' : severity >= 70 ? '#e89234' : '#c4a742';

  return (
    <div className={styles.severityWrap}>
      <div className={styles.severityBar}>
        <div
          className={styles.severityFill}
          style={{ width: `${width}%`, background: color }}
        />
      </div>
      <span className={styles.severityPct} style={{ color }}>
        {severity}%
      </span>
    </div>
  );
};

const Values = ({ onNavigate }) => {
  const [headingRef, isVisible] = useWordReveal({ threshold: 0.3 });
  const cardsRef = useRef(null);
  const [cardsVisible, setCardsVisible] = useState(false);

  useEffect(() => {
    const node = cardsRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCardsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const headingText = 'The Execution Gap Is Costing You Millions';
  const words = headingText.split(' ');

  return (
    <section
      id="challenge"
      className={`${styles.section} ${cardsVisible ? styles.sectionActive : ''}`}
    >
      <CircuitTrace position={{ top: '5%', right: '3%' }} size={200} theme="dark" opacity={0.18} className={styles.doodle} />
      <BrokenChain position={{ bottom: '8%', left: '3%' }} size={160} theme="dark" opacity={0.16} className={styles.doodle} />
      <Microchip position={{ top: '35%', right: '2%' }} size={120} theme="dark" opacity={0.14} className={styles.doodle} />
      <CodeTags position={{ bottom: '12%', right: '5%' }} size={140} theme="dark" opacity={0.15} className={styles.doodle} />
      <DataFlow position={{ top: '12%', left: '2%' }} size={130} theme="dark" opacity={0.14} className={styles.doodle} />
      <IsoBolt position={{ top: '25%', left: '6%' }} size={140} opacity={0.5} />
      <FloatingDiamond position={{ bottom: '25%', right: '8%' }} size={130} opacity={0.45} />

      <div className="container">
        <div className={styles.centered}>
          <span className={styles.sectionTag}>THE EXECUTION GAP</span>
          <div
            ref={headingRef}
            className={`word-reveal${isVisible ? ' visible' : ''}`}
          >
            <h2 className={styles.heading}>
              {words.map((word, i) => (
                <span
                  key={i}
                  className="word"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  {word}
                </span>
              ))}
            </h2>
          </div>
          <p className={styles.description}>
            Most organizations have invested heavily in AI and digital
            transformation. The technology isn't the problem — execution is.
            Projects stall, vendors overpromise, and ROI remains theoretical.
          </p>
        </div>

        {/* ── Cards grid ── */}
        <div ref={cardsRef} className={styles.cardsGrid}>
          {painPoints.map((point, index) => (
            <div
              key={index}
              className={`${styles.painCard} ${cardsVisible ? styles.painCardVisible : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className={styles.cardTop}>
                <span className={styles.cardNumber}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className={styles.cardBadge}>Impact Severity</span>
              </div>

              <p className={styles.cardText}>{point.text}</p>
              <p className={styles.cardImpact}>{point.impact}</p>

              <SeverityBar
                severity={point.severity}
                visible={cardsVisible}
                delay={index * 150}
              />
            </div>
          ))}
        </div>

        <div
          className={`${styles.ctaRow} ${cardsVisible ? styles.ctaVisible : ''}`}
        >
          <button
            className="bracket-btn bracket-btn--dark"
            onClick={() => onNavigate('services')}
          >
            See How We Solve This <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Values;
